import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCheckCircle } from '@sorare/core/src/atoms/icons/system/iconCheckCircle.ts';
import { iconXCircle } from '@sorare/core/src/atoms/icons/system/iconXCircle.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS } from '@sorare/core/src/atoms/typography';

import type { ChampionshipLineupPosition_so5LeaderboardContender } from './__generated__/index.graphql.ts';

const Root = styled(Horizontal)`
  padding: var(--intermediate-unit) var(--triple-unit);
`;
const NoWrapBrandXS = styled(BrandXS)`
  white-space: nowrap;
`;

type Props = {
  topSo5LeaderboardContenders: ChampionshipLineupPosition_so5LeaderboardContender[];
  so5LeaderboardContender: ChampionshipLineupPosition_so5LeaderboardContender;
};
export const ChampionshipLineupPosition = ({
  topSo5LeaderboardContenders,
  so5LeaderboardContender,
}: Props) => {
  const topSo5LeaderboardContendersSlugs = topSo5LeaderboardContenders.map(
    ({ slug }) => slug
  );
  if (
    !topSo5LeaderboardContendersSlugs.includesValue(
      so5LeaderboardContender.slug
    )
  ) {
    return (
      <Root>
        <FontAwesomeIcon icon={iconXCircle} color="var(--c-red-400)" />
        <NoWrapBrandXS>
          <FormattedMessage defaultMessage="Not counted" />
        </NoWrapBrandXS>
      </Root>
    );
  }

  const position =
    topSo5LeaderboardContendersSlugs.indexOf(so5LeaderboardContender.slug) + 1;
  const bestLineup = position === 1;

  return (
    <Root>
      <FontAwesomeIcon icon={iconCheckCircle} color="var(--c-rewards-blue)" />
      <Horizontal gap={0.5}>
        <NoWrapBrandXS
          color={bestLineup ? 'var(--c-white)' : 'var(--c-nd-600)'}
        >
          <FormattedMessage
            defaultMessage="<white>{position, selectordinal, one {#st} two {#nd} few {#rd} other {#th}}</white> highest lineup"
            values={{
              position,
              white: content =>
                !bestLineup && (
                  <span
                    style={{
                      color: 'var(--c-white)',
                    }}
                  >
                    {content}
                  </span>
                ),
            }}
          />
        </NoWrapBrandXS>
      </Horizontal>
    </Root>
  );
};

ChampionshipLineupPosition.fragments = {
  so5LeaderboardContender: gql`
    fragment ChampionshipLineupPosition_so5LeaderboardContender on So5LeaderboardContender {
      slug
    }
  ` as TypedDocumentNode<ChampionshipLineupPosition_so5LeaderboardContender>,
};
